/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DEFAULT_EAS_DISPLAY_MODE,
  EAS_ALERT_MAX_DURATION_MS,
  EAS_PLAYBACK_RESUME_DELAY_MS
} from "../../../config";
import { EasFlowEvent, EasLogger } from "../../logging/EasLogger";
import { Log } from "../../logging/Log";
import { DiagnosticSeverity } from "../../types";
import { EasInputGate } from "../../navigation/EasInputGate";
import { PlayerState } from "../../videoplayer/enums/PlayerState";
import type { TStandalonePlaybackEvent } from "../../videoplayer/types/TStandalonePlaybackEvent";
import { EamAlertType } from "../types";
import type { TEamPushNotification } from "../types";
import { resolveIsDismissible } from "./EasDismissPolicy";
import { EasResumeState } from "./EasResumeState";
import type {
  IEamListener,
  IEasPreemptablePlayback,
  TEasPlaybackProvider
} from "./types";

/**
 * EAS feature model — the terminal owner of an `emergencyAlertMessage`.
 *
 * Reached from PushNotificationRouter. Owns the EAS-specific work that would
 * otherwise be duplicated in every consumer: text sanitisation, the delivery
 * timestamp, and the alert lifecycle.
 *
 * Exposes the feature-level listener tier (IEamListener), which is
 * bidirectional: display and teardown events go out through the listener,
 * and the UI reports completion back through eamDone().
 *
 * ## The alert lifecycle
 *
 * An alert takes the screen, holds it, and gives it back. All three belong
 * here rather than to whatever happens to be rendered, so an alert behaves
 * identically on the playback screen and on Home, Guide, My Hub or Search:
 *
 * 1. **Take** — note whether playback was running, then mute and pause it and
 *    start the alert's own media. Every input is blocked through
 *    `EasInputGate`.
 * 2. **Hold** — until end of media, until the viewer dismisses it, or until
 *    the fallback clock expires. Which of the three apply is decided by the
 *    alert itself; see {@link armExpiryTimer}.
 * 3. **Give back** — release the input gate, tell the listeners (which hides
 *    the OSD and restores the route), then unmute and resume playback.
 *
 * Preemption never stops or unloads the player, so step 3 costs no re-tune
 * and has no per-content-type branch: Live, nDVR, Start Over, Catch Up and
 * VOD all resume the same way, from the playhead the player never gave up.
 */
export class EamModelImpl {
  private static readonly TAG: string = "EamModelImpl";

  /** `eamDone` reason recorded when the viewer dismissed the alert. */
  private static readonly DISMISS_REASON_USER_EXIT: string = "userExit";

  /** `eamDone` reason recorded when the alert media played to its end. */
  private static readonly DISMISS_REASON_END_OF_MEDIA: string = "endOfMedia";

  /** `eamDone` reason recorded when the fallback expiry clock ran out. */
  private static readonly DISMISS_REASON_EXPIRED: string = "expired";

  /** `eamDone` reason recorded when nothing managed to put the alert on screen. */
  private static readonly DISMISS_REASON_NOT_DISPLAYED: string = "notDisplayed";

  /** Replaced with a space so long alerts are not truncated by the UI. */
  private static readonly NEWLINE_SYMBOL: string = "\n";

  /** Stripped outright for the same reason. */
  private static readonly RETURN_SYMBOL: string = "\r";

  /** Replacement for NEWLINE_SYMBOL. */
  private static readonly SPACE_SYMBOL: string = " ";

  private static mInstance: EamModelImpl | null = null;

  /** Feature-level listeners. A Set makes add idempotent. */
  private readonly mEamListeners: Set<IEamListener> = new Set<IEamListener>();

  /** Delivery time of the most recent alert, in epoch milliseconds. */
  private mEamDeliveredTimeMs: number = 0;

  /**
   * Whether an alert is currently on screen.
   *
   * Distinguishes "preempt the content" from "replace an alert already
   * showing", which must not re-capture the resume snapshot. Also the guard
   * that makes {@link eamDone} idempotent, which matters because end of media
   * and a dismiss key can land in either order.
   */
  private mIsAlertActive: boolean = false;

  /** Whether the alert on screen permits the viewer to dismiss it. */
  private mIsActiveAlertDismissible: boolean = false;

  /**
   * Resolves the playback controller when an alert arrives.
   *
   * Injected rather than imported: a value import of
   * `VideoPlaybackController` from this package closes a require cycle
   * through the interactor barrel. See `IEasPreemptablePlayback`. `null`
   * until bootstrap registers one, in which case preemption is skipped and
   * the alert is still displayed.
   */
  private mPlaybackProvider: TEasPlaybackProvider | null = null;

  /**
   * Resolves the EAS display mode (market) when an alert arrives.
   *
   * Needed because dismissibility is a joint decision — see
   * {@link resolveIsDismissible} — and the market half lives in the branding
   * bundle, which the ViewModel owns. A provider rather than a value so the
   * model never holds a stale market across a re-branding, and `null` until
   * post-sign-in startup registers one, in which case the product default
   * applies.
   */
  private mEasDisplayModeProvider: (() => string) | null = null;

  /**
   * Correlation id for the alert on screen, or "" when none is.
   *
   * Threaded through every `eas_flow` diagnostic so one alert's stages can be
   * read as a sequence. A burst interleaves otherwise, and a superseded
   * alert's teardown is then indistinguishable from its successor's.
   */
  private mAlertId: string = "";

  /** Epoch ms at which the alert on screen was received. For its duration. */
  private mAlertStartedAtMs: number = 0;

  /** Fallback expiry timer for an alert with no other way out; see armExpiryTimer. */
  private mExpiryTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Pending delayed resume, or `null` when none is scheduled.
   *
   * Held so an alert arriving inside the resume delay can cancel it — the
   * content must not come back under the new alert. The snapshot deliberately
   * outlives the cancelled timer, so the second alert restores what the first
   * one displaced rather than the paused state it inherited.
   */
  private mResumeTimerId: ReturnType<typeof setTimeout> | null = null;

  /**
   * Subscribed handler for alert-media events, or `null` when not subscribed.
   *
   * Held so the exact reference given to `subscribe()` can be handed back to
   * `unsubscribe()`, and so a second alert does not subscribe twice.
   */
  private mStandaloneHandler:
    | ((event: TStandalonePlaybackEvent) => void)
    | null = null;

  /**
   * Handle a parsed EAS notification: sanitise, stamp, preempt, then notify
   * listeners.
   *
   * The delivery timestamp is recorded for the eamDone() report.
   *
   * @param data - The parsed EAS notification
   */
  public onNotificationReceived(data: TEamPushNotification): void {
    if (!EamModelImpl.isHandledAlertType(data.alertType)) {
      Log.i(
        EamModelImpl.TAG,
        `onNotificationReceived(): alertType=${data.alertType} is not handled ` +
          "by this app — ignored entirely"
      );
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertIgnored,
        "EAS alert type is not handled by this app",
        DiagnosticSeverity.Info,
        undefined,
        { alert_type: data.alertType }
      );
      return;
    }

    const sanitizedText: string = EamModelImpl.sanitizeText(data.text);

    Log.i(
      EamModelImpl.TAG,
      `onNotificationReceived(): alertType=${data.alertType}, contentAvType=${data.contentAvType}, allowUserExit=${data.allowUserExit}, textLength=${sanitizedText.length}, mediaUrl=${EasLogger.sanitizeUrl(data.mediaUrl)}, deliveredTimeMs=${data.receivedTimeMs}, isAlertActive=${String(this.mIsAlertActive)}`
    );
    // Separate line, so a console filtered to the flow stays readable and the
    // alert's wording is still one grep away.
    Log.i(
      EamModelImpl.TAG,
      `onNotificationReceived(): text="${sanitizedText}"`
    );

    // A newer alert always replaces whatever is on screen (UAW-2728). With
    // EAN refused above, every alert that reaches here ranks equally, so there
    // is nothing left to arbitrate — and a genuine re-delivery of the same
    // alert never gets this far, because `PushNotificationModelImpl` drops it
    // on `Origin-RequestId` first.
    //
    // Only the alert that takes the screen from content preempts playback. A
    // replacing alert arrives with playback already muted and paused, so
    // capturing again would snapshot the preempted state and lose whether the
    // viewer was actually watching.
    const isFirstAlert: boolean = !this.mIsAlertActive;

    // One id per alert, so its stages correlate in a log where a burst of
    // alerts interleaves. A superseding alert takes a fresh one; the alert it
    // replaces has already reported its own end through eamDone().
    this.mAlertId = EasLogger.nextAlertId();
    this.mAlertStartedAtMs = Date.now();
    EasLogger.logFlowEvent(
      EasFlowEvent.AlertReceived,
      "EAS alert received",
      DiagnosticSeverity.Info,
      this.mAlertId,
      {
        alert_type: data.alertType,
        content_av_type: data.contentAvType,
        allow_user_exit: String(data.allowUserExit),
        // The alert's own words. Public broadcast content, and the one field
        // that distinguishes "the OSD was blank because nothing arrived" from
        // "the text was lost on the way to the screen".
        text: sanitizedText,
        // Kept alongside the text so an empty alert stays greppable.
        text_length: String(sanitizedText.length),
        // Query string dropped — a signed CDN URL carries its credential
        // there, and diagnostics leave the device.
        media_url: EasLogger.sanitizeUrl(data.mediaUrl),
        has_media_url: String(data.mediaUrl.length > 0)
      }
    );
    if (!isFirstAlert) {
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertSuperseded,
        "EAS alert replaced one already on screen",
        DiagnosticSeverity.Info,
        this.mAlertId
      );
    }

    this.mEamDeliveredTimeMs = data.receivedTimeMs;

    // The expiry clock and the media belong to the alert that armed them, not
    // to the one now replacing it. A resume scheduled by an alert that has
    // just ended is cancelled too — its content must not return underneath
    // this one.
    this.clearExpiryTimer();
    this.clearResumeTimer();
    this.stopAlertMedia();

    // Guards itself on whether a snapshot is already held, which covers both
    // an alert replacing one on screen and an alert arriving inside the
    // previous one's resume delay.
    this.capturePreemptedPlayback();

    this.mIsAlertActive = true;
    // Resolved once, here, and used for all three of the gate, the display and
    // the expiry clock. `data.allowUserExit` is only half the answer: a market
    // whose template shows no dismiss footer has no dismissal to offer, so the
    // wire field alone would let BACK clear an alert nothing on screen said
    // could be cleared.
    const displayMode: string = this.resolveEasDisplayMode();
    this.mIsActiveAlertDismissible = resolveIsDismissible(
      displayMode,
      data.allowUserExit
    );
    EasLogger.logFlowEvent(
      EasFlowEvent.DismissibilityResolved,
      "EAS dismissibility resolved from market and alert",
      DiagnosticSeverity.Info,
      this.mAlertId,
      {
        display_mode: displayMode,
        allow_user_exit: String(data.allowUserExit),
        is_dismissible: String(this.mIsActiveAlertDismissible)
      }
    );

    // Block every input for as long as the alert is on screen. The dismiss
    // key is consumed by the gate and routed back here rather than forwarded
    // to the screen, so dismissing an alert cannot also navigate away from
    // whatever it covered. Released in eamDone() and destroy().
    EasInputGate.getInstance().activate(
      this.mIsActiveAlertDismissible,
      (): void => {
        this.eamDone(EamModelImpl.DISMISS_REASON_USER_EXIT, Date.now());
      }
    );
    EasLogger.logFlowEvent(
      EasFlowEvent.InputBlocked,
      "EAS input gate opened — all input blocked",
      DiagnosticSeverity.Info,
      this.mAlertId,
      { is_dismissible: String(this.mIsActiveAlertDismissible) }
    );

    // The alert's media, its input block and its hold on playback all exist to
    // accompany something on screen. If nothing put it there, none of them may
    // go ahead: starting the tone anyway is an emergency alert the viewer can
    // hear but cannot see, read or dismiss.
    if (!this.notifyEamDisplay(data, sanitizedText)) {
      Log.e(
        EamModelImpl.TAG,
        "onNotificationReceived(): alert reached no display — abandoning it " +
          "rather than playing its audio over nothing"
      );
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertNotDisplayed,
        "EAS alert reached no display — abandoned",
        DiagnosticSeverity.Error,
        this.mAlertId
      );
      this.eamDone(EamModelImpl.DISMISS_REASON_NOT_DISPLAYED, Date.now());
      return;
    }

    EasLogger.logFlowEvent(
      EasFlowEvent.AlertDisplayed,
      "EAS alert is on screen",
      DiagnosticSeverity.Info,
      this.mAlertId
    );

    // Keyed on whether media actually started, not on whether the alert
    // declared a URL: an alert whose media never ran has no end-of-media
    // coming, and must be bounded like a text-only one.
    const hasAlertMedia: boolean = this.beginAlertMedia(data, isFirstAlert);
    this.armExpiryTimer(
      this.mIsActiveAlertDismissible,
      hasAlertMedia ? "playing" : "none"
    );
  }

  /**
   * Whether this app displays alerts of the given type at all.
   *
   * `EAN` is refused outright — no OSD, no preemption, no input block,
   * nothing. An Emergency Action Notification is carried by the broadcast
   * itself rather than presented by the client, so anything this app did with
   * one would be a second, competing presentation of an alert the viewer is
   * already being shown.
   *
   * The refusal is total and applies at any moment, including while another
   * alert is on screen: an EAN never displaces, dismisses or alters an alert
   * that is already up, it is simply dropped.
   *
   * @param alertType - The alert type off the wire
   * @returns Whether the EAS flow should run for it
   */
  private static isHandledAlertType(alertType: EamAlertType): boolean {
    return alertType !== EamAlertType.EAN;
  }

  /**
   * Record the playback state the alert is about to overwrite, and flush the
   * viewer's resume point.
   *
   * The alert mutes and pauses rather than stopping, so the player keeps the
   * content, the session and the playhead throughout. What it cannot tell us
   * afterwards is the three things preemption overwrites: whether playback
   * was already paused, whether that pause was the viewer's own
   * ({@link TEasResumeSnapshot.wasForcePaused}), and whether they had already
   * muted ({@link TEasResumeSnapshot.wasMuted}).
   *
   * The last viewed position is flushed for a different reason: the viewer may
   * close the app while the alert is up, and an unwritten position is a resume
   * point lost for good. It is a no-op for live content by construction.
   *
   * The live auto-resume timer is suppressed here too, because this is the
   * last point before the alert pauses anything.
   *
   * A no-op when a snapshot is already held. That one guard covers both ways
   * a second alert can arrive: replacing one still on screen, and arriving
   * inside the previous alert's resume delay. In each case playback is already
   * preempted, so re-reading it would record `wasPlaying: false` and strand
   * the viewer on a paused player once the alerts finally clear.
   */
  private capturePreemptedPlayback(): void {
    if (EasResumeState.getInstance().get() !== null) {
      Log.d(
        EamModelImpl.TAG,
        "capturePreemptedPlayback(): snapshot already held — keeping it"
      );
      return;
    }

    if (this.mPlaybackProvider === null) {
      Log.w(
        EamModelImpl.TAG,
        "capturePreemptedPlayback(): no playback provider registered — " +
          "alert will display over running content"
      );
      return;
    }

    // Everything here is inside the guard, the snapshot included. The
    // snapshot reads straight through to the Shaka player, and those getters
    // are not contractually safe to call with no media loaded — an alert
    // arriving on a screen with no playback must not be lost to a throw from
    // getCurrentTime().
    try {
      const controller: IEasPreemptablePlayback = this.mPlaybackProvider();

      const snapshot = EasResumeState.getInstance().capture(controller);
      EasLogger.logFlowEvent(
        EasFlowEvent.PlaybackPreempted,
        "EAS captured the playback it is about to preempt",
        DiagnosticSeverity.Info,
        this.mAlertId,
        {
          content_type: String(snapshot.playbackContentType),
          was_playing: String(snapshot.wasPlaying),
          was_force_paused: String(snapshot.wasForcePaused),
          was_muted: String(snapshot.wasMuted)
        }
      );

      // Before anything pauses. A forced pause on live content arms a timer
      // that resumes playback once the pause point falls out of the timeshift
      // buffer; for a viewer already far behind live that can fire while the
      // alert is still up, putting the programme back under the overlay and
      // clearing the forced flag the alert depends on. Suppressing first means
      // the timer is never armed rather than armed and cancelled.
      controller.setLiveAutoResumeSuppressed(true);

      if (controller.getPlayable() === null) {
        Log.i(
          EamModelImpl.TAG,
          "capturePreemptedPlayback(): nothing playing — nothing to flush"
        );
        return;
      }

      controller.updateLastViewedPosition();
    } catch (error: unknown) {
      EasLogger.logFlowFailure(
        EasFlowEvent.PlaybackPreemptionFailed,
        "EAS preemption failed — alert will display over running content",
        error,
        this.mAlertId
      );
      // An alert must reach the screen even if preemption fails — a viewer
      // who sees the alert over still-running content is far better served
      // than one who sees no alert at all.
      Log.e(EamModelImpl.TAG, "capturePreemptedPlayback()", error);
    }
  }

  /**
   * Silence the content and give the alert its voice.
   *
   * An alert with a `mediaUrl` plays it on the dedicated standalone element,
   * and that call mutes and force-pauses main playback as a side effect. An
   * alert without one is text only, and then main playback is paused here
   * instead — the viewer must not hear a programme continuing underneath an
   * emergency alert in either case.
   *
   * @param data - The parsed EAS notification
   * @param isFirstAlert - False when this alert replaces one already on
   *   screen, in which case main playback is already muted and paused
   * @returns Whether alert media actually started, and so whether an end of
   *   media can be expected to finish this alert
   */
  private beginAlertMedia(
    data: TEamPushNotification,
    isFirstAlert: boolean
  ): boolean {
    if (this.mPlaybackProvider === null) {
      return false;
    }

    try {
      const controller: IEasPreemptablePlayback = this.mPlaybackProvider();

      if (data.mediaUrl.length === 0) {
        if (isFirstAlert) {
          Log.i(
            EamModelImpl.TAG,
            "beginAlertMedia(): text-only alert — muting and pausing playback"
          );
          // Mute and pause, the two distinct steps the requirements name. A
          // media-bearing alert gets both from startStandalonePlayback(); a
          // text-only one never reaches it, so they are applied here.
          //
          // Forced, so playback cannot auto-resume underneath the alert. The
          // viewer's own resume clears it.
          controller.mute();
          controller.pause(true);
        }
        return false;
      }

      // Subscribed before the media starts, so a source short enough to end
      // between the two calls cannot be missed.
      this.subscribeAlertMedia(controller);

      Log.i(EamModelImpl.TAG, "beginAlertMedia(): starting alert media");
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertMediaStarted,
        "EAS alert media requested",
        DiagnosticSeverity.Info,
        this.mAlertId,
        {
          content_av_type: data.contentAvType,
          media_url: EasLogger.sanitizeUrl(data.mediaUrl)
        }
      );
      controller
        .startStandalonePlayback(data.mediaUrl)
        .catch((error: unknown): void => {
          Log.e(
            EamModelImpl.TAG,
            "beginAlertMedia(): startStandalonePlayback()",
            error
          );
          this.onAlertMediaFailed();
        });
      return true;
    } catch (error: unknown) {
      Log.e(EamModelImpl.TAG, "beginAlertMedia()", error);
      return false;
    }
  }

  /**
   * Stop the alert media. Unconditional, and safe when none was started.
   *
   * This model deliberately keeps no record of whether it started media, and
   * so never decides whether a stop is needed. Every such record was a way to
   * get the answer wrong — a source that errored but kept playing, a flag
   * cleared by an event rather than by the stop itself — and getting it wrong
   * meant an alert tone carrying on over the screen the viewer was returned
   * to. `stopStandalonePlayback()` is idempotent, so asking for a stop that
   * was not needed costs nothing, while failing to ask costs the viewer.
   *
   * Reports no end of media, so tearing an alert down can never be mistaken
   * for the alert having run its course.
   */
  private stopAlertMedia(): void {
    if (this.mPlaybackProvider === null) {
      return;
    }
    try {
      this.mPlaybackProvider().stopStandalonePlayback();
    } catch (error: unknown) {
      Log.e(EamModelImpl.TAG, "stopAlertMedia()", error);
    }
  }

  /**
   * Subscribe to alert-media lifecycle events, once.
   *
   * Taken straight off the controller rather than read back out of Redux:
   * end of media ends the alert and gives the viewer their content back, and
   * that must hold whether or not any View is mounted to relay it.
   *
   * @param controller - The playback controller to subscribe to
   */
  private subscribeAlertMedia(controller: IEasPreemptablePlayback): void {
    if (this.mStandaloneHandler !== null) {
      return;
    }
    const handler = (event: TStandalonePlaybackEvent): void => {
      this.onAlertMediaEvent(event);
    };
    controller.getStandalonePlaybackEventListener().subscribe(handler);
    this.mStandaloneHandler = handler;
    Log.d(EamModelImpl.TAG, "subscribeAlertMedia(): subscribed");
  }

  /** Drop the alert-media subscription. Idempotent. */
  private unsubscribeAlertMedia(): void {
    const handler = this.mStandaloneHandler;
    if (handler === null || this.mPlaybackProvider === null) {
      this.mStandaloneHandler = null;
      return;
    }
    this.mStandaloneHandler = null;
    try {
      this.mPlaybackProvider()
        .getStandalonePlaybackEventListener()
        .unsubscribe(handler);
    } catch (error: unknown) {
      Log.e(EamModelImpl.TAG, "unsubscribeAlertMedia()", error);
    }
  }

  /**
   * React to the alert media's own lifecycle.
   *
   * End of media ends the alert — that is this path's whole purpose, and it
   * applies whether or not the alert was dismissible.
   *
   * A media *failure* deliberately does not. A source that failed never
   * raises `ended`, so nothing else will end the alert, but the alert text is
   * the accessible form of the message and cutting it off the instant the
   * audio fails would leave an emergency on screen for a few frames. A failed
   * alert is therefore treated exactly like one that never had media: the
   * viewer's dismiss key ends it, or the fallback clock does when there is no
   * dismiss key.
   *
   * @param event - The event raised by the standalone player
   */
  private onAlertMediaEvent(event: TStandalonePlaybackEvent): void {
    if (!this.mIsAlertActive) {
      return;
    }

    if (event.state === PlayerState.PLAYING) {
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertMediaPlaying,
        "EAS alert media is playing",
        DiagnosticSeverity.Info,
        this.mAlertId
      );
      return;
    }

    if (event.state === PlayerState.ENDED) {
      Log.i(
        EamModelImpl.TAG,
        "onAlertMediaEvent(): end of media — finishing alert"
      );
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertMediaEnded,
        "EAS alert media reached end of media",
        DiagnosticSeverity.Info,
        this.mAlertId
      );
      this.eamDone(EamModelImpl.DISMISS_REASON_END_OF_MEDIA, Date.now());
      return;
    }

    if (event.state === PlayerState.ERROR) {
      Log.w(
        EamModelImpl.TAG,
        `onAlertMediaEvent(): alert media failed (${String(event.errorMessage)})`
      );
      EasLogger.logFlowEvent(
        EasFlowEvent.AlertMediaFailed,
        "EAS alert media failed — text stays on screen",
        DiagnosticSeverity.Warning,
        this.mAlertId
      );
      this.onAlertMediaFailed();
    }
  }

  /**
   * Handle alert media that will never reach its end, however it failed —
   * a player error event, or a rejected start.
   *
   * The alert is not finished here; it falls back to the same rule as one
   * that never had media — except that the fallback clock is armed either
   * way, because a stream that will never end is exactly what that clock is
   * for.
   */
  private onAlertMediaFailed(): void {
    if (!this.mIsAlertActive) {
      return;
    }
    Log.w(
      EamModelImpl.TAG,
      "onAlertMediaFailed(): alert text stays up, bounded by the fallback clock"
    );
    // The stop is deliberately still owed: a failed element is not
    // necessarily a silent one, and eamDone() is what guarantees it.
    this.armExpiryTimer(this.mIsActiveAlertDismissible, "failed");
  }

  /**
   * Arm the fallback clock, but only for an alert that has no other way to
   * end.
   *
   * The EasAlert schema declares no TTL, duration or expiry field, so an
   * alert that is neither dismissible nor media-bearing has nothing that can
   * ever finish it — and because `EasInputGate` blocks every key for the
   * alert's lifetime, that is an app no viewer can recover without a restart.
   * {@link EAS_ALERT_MAX_DURATION_MS} bounds exactly that case.
   *
   * Two cases are bounded, and they are not the same case:
   *
   * - **`"failed"`** — the alert's media failed to load or errored, so no end
   *   of media is ever coming. Bounded *whether or not the viewer can dismiss
   *   it*: the requirements call for a fallback timer on stream failure
   *   precisely because an overlay left waiting on an event that will never
   *   arrive blocks the UI indefinitely, and a dismiss key only helps a viewer
   *   who is present and willing to press it.
   * - **`"none"` and not dismissible** — the alert never had media, and the
   *   schema declares no TTL, so nothing else can finish it. Because
   *   `EasInputGate` blocks every key for the alert's lifetime, that is an app
   *   no viewer can recover without a restart.
   *
   * A `"none"` alert the viewer *can* dismiss is deliberately left unbounded:
   * it has a defined way out, and how long a genuine emergency holds the
   * screen is the server's decision, not a client-side clock's.
   *
   * @param isDismissible - Whether the viewer may dismiss this alert
   * @param mediaState - What the alert's own media is doing
   */
  private armExpiryTimer(
    isDismissible: boolean,
    mediaState: "playing" | "failed" | "none"
  ): void {
    this.clearExpiryTimer();

    // Media that is playing will report its end, which is the alert's way out.
    if (mediaState === "playing") {
      return;
    }

    if (mediaState === "none" && isDismissible) {
      return;
    }

    Log.i(
      EamModelImpl.TAG,
      `armExpiryTimer(): mediaState=${mediaState}, dismissible=${String(isDismissible)} — ` +
        `alert bounded to ${EAS_ALERT_MAX_DURATION_MS}ms`
    );
    EasLogger.logFlowEvent(
      EasFlowEvent.ExpiryArmed,
      "EAS fallback expiry clock armed",
      DiagnosticSeverity.Info,
      this.mAlertId,
      {
        media_state: mediaState,
        is_dismissible: String(isDismissible),
        duration_ms: String(EAS_ALERT_MAX_DURATION_MS)
      }
    );
    this.mExpiryTimerId = setTimeout((): void => {
      this.mExpiryTimerId = null;
      Log.i(EamModelImpl.TAG, "armExpiryTimer(): alert expired");
      EasLogger.logFlowEvent(
        EasFlowEvent.ExpiryFired,
        "EAS fallback expiry clock ran out",
        DiagnosticSeverity.Warning,
        this.mAlertId
      );
      this.eamDone(EamModelImpl.DISMISS_REASON_EXPIRED, Date.now());
    }, EAS_ALERT_MAX_DURATION_MS);
  }

  /** Cancel the fallback clock. Idempotent. */
  private clearExpiryTimer(): void {
    if (this.mExpiryTimerId === null) {
      return;
    }
    clearTimeout(this.mExpiryTimerId);
    this.mExpiryTimerId = null;
  }

  /**
   * Cancel a pending delayed resume. Idempotent.
   *
   * The snapshot is deliberately left in place: a cancelled resume means the
   * content has not come back yet, so what it describes is still owed to the
   * viewer.
   */
  private clearResumeTimer(): void {
    if (this.mResumeTimerId === null) {
      return;
    }
    clearTimeout(this.mResumeTimerId);
    this.mResumeTimerId = null;
    Log.d(EamModelImpl.TAG, "clearResumeTimer(): pending resume cancelled");
  }

  /**
   * Report that the alert finished, notify listeners, and put the viewer's
   * content back.
   *
   * Reached three ways — the dismiss key, end of media, and the fallback
   * expiry clock — and any two of them can race, so the first one to arrive
   * wins and the rest are ignored.
   *
   * Listeners are told before playback is restored: the OSD comes down and
   * the route goes back first, so the picture returns to the screen the
   * viewer is actually returning to.
   *
   * @param reason - Why the alert finished ("userExit", "endOfMedia",
   *   "expired", "osdCleared", "notDisplayed")
   * @param dismissedAtMs - When the alert finished, epoch ms. Named for what
   *   every caller actually passes: the dismiss key, end of media and the
   *   expiry clock all report the moment of dismissal, not the moment of
   *   display. The delivery time is held separately, in
   *   `mEamDeliveredTimeMs`, and the two are logged together so the alert's
   *   on-screen duration can still be recovered.
   */
  public eamDone(reason: string, dismissedAtMs: number): void {
    if (!this.mIsAlertActive) {
      Log.d(
        EamModelImpl.TAG,
        `eamDone(): reason=${reason} — no alert on screen, ignoring`
      );
      return;
    }

    Log.i(
      EamModelImpl.TAG,
      `eamDone(): reason=${reason}, deliveredTimeMs=${this.mEamDeliveredTimeMs}, dismissedAtMs=${dismissedAtMs}`
    );

    const alertId: string = this.mAlertId;
    const onScreenMs: number =
      this.mAlertStartedAtMs > 0 ? Date.now() - this.mAlertStartedAtMs : 0;

    EasLogger.logFlowEvent(
      EasFlowEvent.AlertFinished,
      "EAS alert finished",
      DiagnosticSeverity.Info,
      alertId,
      { reason: reason, on_screen_ms: String(onScreenMs) }
    );

    this.clearExpiryTimer();
    this.stopAlertMedia();
    this.mIsAlertActive = false;
    this.mIsActiveAlertDismissible = false;
    EasInputGate.getInstance().deactivate();
    EasLogger.logFlowEvent(
      EasFlowEvent.InputReleased,
      "EAS input gate closed — input routing restored",
      DiagnosticSeverity.Info,
      alertId
    );

    this.notifyEamDone();
    this.restorePreemptedPlayback();

    this.mAlertId = "";
    this.mAlertStartedAtMs = 0;
  }

  /**
   * Put the viewer's content back, and drop the snapshot.
   *
   * An unmute and a resume, because the alert only ever muted and paused:
   * the player still holds the content, the session and the playhead, so
   * there is nothing to re-acquire and nothing to seek. That is what makes
   * this one path correct for Live, nDVR, Start Over, Catch Up and VOD alike.
   *
   * ## What decides whether playback resumes
   *
   * {@link TEasResumeSnapshot.wasForcePaused}, not `wasPlaying`. The forced
   * flag is set only by a deliberate viewer pause and by this alert, so it is
   * the one reading that separates "the viewer paused this" from "a subsystem
   * paused it and means to resume it". Keying on `wasPlaying` instead left
   * every automatic pause — Hidden, network loss, backgrounding, restrictions
   * — parked behind the alert's own forced flag with nothing left to clear
   * it, which stranded playback paused for the rest of the session.
   *
   * View mode needs no restoring either — the alert never changed it, so the
   * screen still owns whichever player surface it had: full screen on the
   * playback screen, PIG, Atmospheric or Hidden elsewhere. A Hidden player
   * records the resume intent and replays it when a screen next claims a
   * visible mode, which is the app's existing behaviour and exactly right
   * here.
   *
   * The resume itself is held back by {@link EAS_PLAYBACK_RESUME_DELAY_MS},
   * so the programme's audio does not come back while the alert tone is still
   * unwinding and the overlay is still painted.
   *
   * The snapshot is dropped once the restore has actually run — not when it is
   * scheduled. An alert arriving inside the delay cancels the pending resume
   * and inherits the snapshot, so the content the *first* alert displaced is
   * what eventually comes back.
   */
  private restorePreemptedPlayback(): void {
    const resumeState: EasResumeState = EasResumeState.getInstance();
    const snapshot = resumeState.get();

    if (snapshot === null) {
      Log.d(
        EamModelImpl.TAG,
        "restorePreemptedPlayback(): no snapshot — nothing to restore"
      );
      return;
    }

    if (this.mPlaybackProvider === null) {
      Log.w(
        EamModelImpl.TAG,
        "restorePreemptedPlayback(): no playback provider registered — " +
          "playback cannot be restored"
      );
      resumeState.clear();
      return;
    }

    Log.i(
      EamModelImpl.TAG,
      `restorePreemptedPlayback(): ` +
        `contentType=${String(snapshot.playbackContentType)}, ` +
        `channel=${snapshot.channelNumber}, ` +
        `capturedPositionSec=${String(Math.round(snapshot.positionSec))}, ` +
        `wasPlaying=${String(snapshot.wasPlaying)}, ` +
        `wasForcePaused=${String(snapshot.wasForcePaused)}, ` +
        `wasMuted=${String(snapshot.wasMuted)}, ` +
        `heldForMs=${String(Date.now() - snapshot.capturedAtMs)}`
    );

    // Captured for the delayed resume below: by the time that fires, the
    // alert has finished and `mAlertId` has been cleared for the next one.
    const alertId: string = this.mAlertId;

    try {
      const controller: IEasPreemptablePlayback = this.mPlaybackProvider();

      // Lifted first, and unconditionally: the alert armed nothing, it only
      // inhibited, so handing the timer back costs nothing even on a path
      // that resumes immediately afterwards.
      controller.setLiveAutoResumeSuppressed(false);

      // Immediate, unlike the resume: audio cannot escape from a player that
      // is still paused, and leaving it muted would silence the viewer's own
      // resume if they got there first. Skipped when the viewer had muted
      // before the alert — restoring their state means leaving them silent.
      if (snapshot.wasMuted) {
        Log.i(
          EamModelImpl.TAG,
          "restorePreemptedPlayback(): was muted before the alert — left muted"
        );
      } else {
        controller.unmute();
      }

      if (snapshot.wasForcePaused) {
        // The viewer had paused deliberately — `togglePlayPause` is the only
        // other caller that forces — so they are put back paused, with the
        // flag they set left exactly as it was.
        Log.i(
          EamModelImpl.TAG,
          "restorePreemptedPlayback(): viewer had paused — left paused"
        );
        EasLogger.logFlowEvent(
          EasFlowEvent.PlaybackLeftAsFound,
          "EAS left playback paused — the viewer had paused it",
          DiagnosticSeverity.Info,
          alertId,
          { was_muted: String(snapshot.wasMuted) }
        );
        resumeState.clear();
        return;
      }

      if (snapshot.playbackContentType === null) {
        // Nothing was loaded, so there is nothing to put back. The forced flag
        // the alert applied is cleared by `cleanPlayer()` on the next load.
        Log.i(
          EamModelImpl.TAG,
          "restorePreemptedPlayback(): nothing was loaded — nothing to resume"
        );
        EasLogger.logFlowEvent(
          EasFlowEvent.PlaybackLeftAsFound,
          "EAS had nothing to resume — no content was loaded",
          DiagnosticSeverity.Info,
          alertId
        );
        resumeState.clear();
        return;
      }

      // Everything else resumes, including a player that was not playing when
      // the alert arrived. `wasPlaying: false` with `wasForcePaused: false`
      // means some subsystem had paused it and was relying on its own
      // auto-resume — Hidden, network loss, backgrounding, restrictions — and
      // every one of those paths bails out on the forced flag the alert just
      // set. Only `resume()` clears it, so skipping the resume here would
      // strand playback paused for the rest of the session. On a Hidden player
      // `resume()` records the intent and defers, which is exactly right.
      this.mResumeTimerId = setTimeout((): void => {
        this.mResumeTimerId = null;
        EasResumeState.getInstance().clear();
        try {
          controller.resume();
          Log.i(EamModelImpl.TAG, "restorePreemptedPlayback(): resumed");
          EasLogger.logFlowEvent(
            EasFlowEvent.PlaybackRestored,
            "EAS restored the content it preempted",
            DiagnosticSeverity.Info,
            alertId,
            {
              content_type: String(snapshot.playbackContentType),
              was_playing: String(snapshot.wasPlaying),
              unmuted: String(!snapshot.wasMuted)
            }
          );
        } catch (error: unknown) {
          Log.e(
            EamModelImpl.TAG,
            "restorePreemptedPlayback(): resume()",
            error
          );
          EasLogger.logFlowFailure(
            EasFlowEvent.PlaybackRestored,
            "EAS could not resume the content it preempted",
            error,
            alertId
          );
        }
      }, EAS_PLAYBACK_RESUME_DELAY_MS);
    } catch (error: unknown) {
      Log.e(EamModelImpl.TAG, "restorePreemptedPlayback()", error);
      resumeState.clear();
    }
  }

  /**
   * Whether this model still considers an alert to be on screen.
   *
   * Exists so the ViewModel can detect the one state that must never persist:
   * the OSD gone while the model still holds an alert open. That combination
   * leaves the alert's audio playing and every key dead over whatever screen
   * the viewer was returned to, and it arises whenever something clears the
   * OSD without routing through {@link eamDone}.
   *
   * @returns True while an alert is open
   */
  public isAlertActive(): boolean {
    return this.mIsAlertActive;
  }

  /**
   * The market whose EAS OSD template is in use.
   *
   * @returns The display mode, or the product default when none is registered
   */
  private resolveEasDisplayMode(): string {
    if (this.mEasDisplayModeProvider === null) {
      Log.w(
        EamModelImpl.TAG,
        "resolveEasDisplayMode(): no provider registered — falling back to " +
          `"${DEFAULT_EAS_DISPLAY_MODE}"`
      );
      return DEFAULT_EAS_DISPLAY_MODE;
    }
    try {
      return this.mEasDisplayModeProvider();
    } catch (error: unknown) {
      Log.e(EamModelImpl.TAG, "resolveEasDisplayMode()", error);
      return DEFAULT_EAS_DISPLAY_MODE;
    }
  }

  /**
   * Register how to read the EAS display mode (market) when an alert arrives.
   *
   * Called from `postSigninStartupSequence`, after the branding bundle has
   * landed. Without it the product default applies, which is logged as a
   * warning rather than passing silently — getting the market wrong changes
   * whether an alert can be dismissed at all.
   *
   * @param provider - Reads `easConfiguration.easDisplayMode` at alert time
   */
  public setEasDisplayModeProvider(provider: () => string): void {
    this.mEasDisplayModeProvider = provider;
    Log.i(EamModelImpl.TAG, "setEasDisplayModeProvider(): registered");
  }

  /**
   * Register how to reach the playback controller when an alert arrives.
   *
   * Called from `postSigninStartupSequence`, and so on every sign-in rather
   * than once per process. Re-registering is harmless: the provider resolves
   * the same controller singleton, and it is read at alert time rather than
   * held.
   *
   * Without it an alert still displays, but the content it covers keeps
   * playing underneath and nothing is restored afterwards — which is why the
   * omission is logged as a warning rather than passing silently.
   *
   * @param provider - Resolves the controller at alert time
   */
  public setPlaybackProvider(provider: TEasPlaybackProvider): void {
    this.mPlaybackProvider = provider;
    Log.i(EamModelImpl.TAG, "setPlaybackProvider(): registered");
  }

  /**
   * Register a feature-level listener. Idempotent for the same reference.
   *
   * @param listener - The listener to add
   */
  public addEamListener(listener: IEamListener): void {
    this.mEamListeners.add(listener);
    Log.d(
      EamModelImpl.TAG,
      `addEamListener(): count=${this.mEamListeners.size}`
    );
  }

  /**
   * Deregister a feature-level listener. A no-op when not registered.
   *
   * @param listener - The listener to remove
   */
  public removeEamListener(listener: IEamListener): void {
    this.mEamListeners.delete(listener);
    Log.d(
      EamModelImpl.TAG,
      `removeEamListener(): count=${this.mEamListeners.size}`
    );
  }

  /**
   * Tear down any displayed alert and drop all listeners. Idempotent.
   */
  public destroy(): void {
    // Released before the listeners are told, so an input block can never
    // outlive the alert that raised it — an app left with every key dead
    // would need a restart to recover.
    this.clearExpiryTimer();
    this.clearResumeTimer();
    this.stopAlertMedia();
    this.unsubscribeAlertMedia();
    this.mIsAlertActive = false;
    this.mIsActiveAlertDismissible = false;
    EasInputGate.getInstance().deactivate();

    // Dropped rather than acted on: destroy() tears the feature down, and the
    // app state a snapshot describes has not survived to be put back.
    EasResumeState.getInstance().clear();

    const listeners: IEamListener[] = Array.from(this.mEamListeners);
    this.mEamListeners.clear();

    for (const listener of listeners) {
      try {
        listener.onCloseEam();
      } catch (error: unknown) {
        Log.e(EamModelImpl.TAG, "destroy(): Listener onCloseEam()", error);
      }
    }

    this.mEamDeliveredTimeMs = 0;
    Log.d(EamModelImpl.TAG, `destroy(): Closed ${listeners.length} listeners`);
  }

  /**
   * Notify every listener that an alert must be displayed.
   *
   * Iterates a snapshot so a listener that deregisters itself during the
   * callback cannot corrupt iteration, and isolates each callback so one
   * failing listener cannot starve the rest.
   *
   * Reports whether the alert actually reached a display, which is a stricter
   * question than whether any listener was registered. A listener that throws
   * has not displayed anything — `EamBridge` reads the branding slice before
   * it dispatches, so a throw there is a real possibility — and the caller
   * needs to distinguish that from success, because everything else the alert
   * does is only justified by it being on screen.
   *
   * @param data - The parsed EAS notification
   * @param text - The sanitised alert text
   * @returns True when at least one listener accepted the alert
   */
  private notifyEamDisplay(data: TEamPushNotification, text: string): boolean {
    const listeners: IEamListener[] = Array.from(this.mEamListeners);

    if (listeners.length === 0) {
      Log.w(
        EamModelImpl.TAG,
        "notifyEamDisplay(): No listeners registered — alert not displayed"
      );
      return false;
    }

    let isDisplayed: boolean = false;
    for (const listener of listeners) {
      try {
        listener.onDisplayEam(
          text,
          this.mIsActiveAlertDismissible,
          data.mediaUrl,
          data.contentAvType,
          data.receivedTimeMs
        );
        isDisplayed = true;
      } catch (error: unknown) {
        Log.e(
          EamModelImpl.TAG,
          "notifyEamDisplay(): Listener onDisplayEam()",
          error
        );
      }
    }
    return isDisplayed;
  }

  /**
   * Notify every listener that the alert finished.
   */
  private notifyEamDone(): void {
    const listeners: IEamListener[] = Array.from(this.mEamListeners);

    for (const listener of listeners) {
      try {
        listener.onDoneReadingEam();
      } catch (error: unknown) {
        Log.e(
          EamModelImpl.TAG,
          "notifyEamDone(): Listener onDoneReadingEam()",
          error
        );
      }
    }
  }

  /**
   * Get the shared instance, creating it on first use.
   *
   * @returns The singleton instance
   */
  public static getInstance(): EamModelImpl {
    if (EamModelImpl.mInstance === null) {
      EamModelImpl.mInstance = new EamModelImpl();
    }
    return EamModelImpl.mInstance;
  }

  /**
   * Discard the shared instance so the next getInstance() builds a fresh one.
   * Test-only.
   */
  public static __resetForTest(): void {
    EamModelImpl.mInstance = null;
  }

  /**
   * Collapse line breaks so the UI does not truncate the alert.
   *
   * @param text - The raw alert text
   * @returns The sanitised text
   */
  private static sanitizeText(text: string): string {
    return text
      .split(EamModelImpl.NEWLINE_SYMBOL)
      .join(EamModelImpl.SPACE_SYMBOL)
      .split(EamModelImpl.RETURN_SYMBOL)
      .join("");
  }
}
